import { useMemo, useState } from 'react'

import Tabs, { Tab, TabWithCount } from 'common/components/tabs'
import { useAppSelector } from 'common/hooks/redux'

export type VariantStatus = 'pending' | 'live' | 'dropped'

type Props = {
  selectedStatus: VariantStatus
  onStatusChange: (status: VariantStatus) => void
  selectedElement: any
  statusCounts?: {
    pending: number
    live: number
    dropped: number
  }
}

const VariantStatusFilters = ({
  selectedStatus,
  onStatusChange,
  selectedElement,
  statusCounts: propStatusCounts,
}: Props) => {
  // Track active tab locally so rc-tabs always sees a change when switching tabs,
  // even if the parent hasn't updated the prop yet (optimistic update)
  const [activeTab, setActiveTab] = useState<VariantStatus>(selectedStatus)
  const subjectLines = useAppSelector(
    (state) => state.campaignStates.subjectLines
  )

  const statusCounts = useMemo(() => {
    if (propStatusCounts) {
      return propStatusCounts
    }
    const variants =
      selectedElement?.personalized_variants || subjectLines || []
    if (!variants || variants.length === 0) {
      return {
        pending: 0,
        live: 0,
        dropped: 0,
      }
    }

    const counts = {
      pending: 0,
      live: 0,
      dropped: 0,
    }

    variants.forEach((variant: any) => {
      const status = variant.status || variant.bandit_status?.status
      if (status === 'pending') {
        counts.pending++
      } else if (status === 'live' || status === 'approved') {
        counts.live++
      } else if (status === 'dropped') {
        counts.dropped++
      }
    })

    return counts
  }, [propStatusCounts, selectedElement, subjectLines])

  const isPersonalize = !!selectedElement?.personalization_id

  const tabs = useMemo<Tab<VariantStatus>[]>(
    () => [
      {
        key: 'pending',
        name: 'New language',
        menu: (
          <TabWithCount label="New language" count={statusCounts.pending} />
        ),
        content: <div />,
      },
      {
        key: 'live',
        name: 'Live',
        menu: <TabWithCount label="Live" count={statusCounts.live} />,
        content: <div />,
      },
      ...(isPersonalize
        ? []
        : [
            {
              key: 'dropped' as VariantStatus,
              name: 'Dropped',
              menu: (
                <TabWithCount label="Dropped" count={statusCounts.dropped} />
              ),
              content: <div />,
            },
          ]),
    ],
    [statusCounts, isPersonalize]
  )

  const handleTabClick = (tabKey: VariantStatus) => {
    setActiveTab(tabKey)
    onStatusChange(tabKey)
  }

  return (
    <div className="mb-6">
      <Tabs
        tabs={tabs}
        activeKey={activeTab}
        onTabClick={handleTabClick}
        variant="filter"
        className="border-b border-coolGray-200"
      />
    </div>
  )
}

export default VariantStatusFilters
